// ─── Vérifier qu'un relevé va dans le BON compte (module PUR) ─────
// Importer un relevé dans le mauvais compte fausse deux soldes d'un coup et se
// remarque tard. On vérifie donc AVANT d'écrire, et on distingue :
//   • BLOCAGE   — preuve d'incohérence : on n'importe pas.
//   • ATTENTION — indice, pas preuve : on prévient, l'utilisateur décide.
//
// Le contenu prime sur le nom du fichier : un OFX porte le numéro de compte
// (`ACCTID`), c'est une preuve ; un nom de fichier n'est qu'un indice, que
// l'utilisateur peut avoir oublié de changer.

const cents = (m) => Math.round(Math.abs(Number(m) || 0) * 100);

// 4 chiffres consécutifs dans le nom du fichier (souvent la fin du numéro de
// compte quand la banque la met — beaucoup ne la mettent pas du tout).
export function fin4DuNom(nomFichier) {
  const groupes = String(nomFichier || '').match(/\d{4,}/g) || [];
  // On ignore ce qui ressemble à une DATE (année ou AAAAMMJJ), sinon
  // « releve_20260806.csv » ferait croire à un numéro de compte.
  const utiles = groupes.filter(g => !/^(19|20)\d{2}$/.test(g) && !/^(19|20)\d{6}$/.test(g));
  return utiles.length ? utiles[utiles.length - 1].slice(-4) : '';
}

// Résumé de ce qui sera importé : de quoi reconnaître son relevé d'un coup d'œil.
export function resumeReleve(operations = []) {
  const dates = operations.map(o => o.date).filter(Boolean).sort();
  let debits = 0, credits = 0;
  for (const o of operations) {
    if ((o.montant || 0) < 0) debits += Math.abs(o.montant); else credits += o.montant || 0;
  }
  return {
    nb: operations.length,
    du: dates[0] || '', au: dates[dates.length - 1] || '',
    debits: Math.round(debits * 100) / 100,
    credits: Math.round(credits * 100) / 100,
  };
}

// compte : le compte choisi · acctid : lu dans le relevé (OFX) · nomFichier ·
// operations : lues du fichier · existantes : opérations déjà en base.
export function verifierCoherence({ compte, acctid = '', nomFichier = '', operations = [], existantes = [] }) {
  const messages = [];
  let niveau = 'ok';
  const monter = (n) => { if (n === 'blocage' || (n === 'attention' && niveau === 'ok')) niveau = n; };

  if (!compte) {
    monter('blocage');
    messages.push({ niveau: 'blocage', cle: 'finances.vChoisirCompte' });
    return { niveau, messages, resume: resumeReleve(operations) };
  }

  // 1. PREUVE : le relevé porte un numéro de compte connu, et ce n'est pas
  //    celui-là. (Un compte sans `acctids` n'a encore jamais été importé : on
  //    ne peut rien affirmer, l'association se fera après cet import.)
  const connus = compte.acctids || [];
  if (acctid && connus.length && !connus.includes(String(acctid))) {
    monter('blocage');
    messages.push({ niveau: 'blocage', cle: 'finances.vAutreCompte', params: { acctid } });
  }

  // 2. INDICE : 4 chiffres dans le nom du fichier qui ne collent pas.
  const fin4 = fin4DuNom(nomFichier);
  if (fin4 && compte.fin4 && fin4 !== compte.fin4) {
    monter('attention');
    messages.push({ niveau: 'attention', cle: 'finances.vNomFichier', params: { fin4, attendu: compte.fin4 } });
  }

  // 3. INDICE : ces opérations existent déjà sur un AUTRE compte — signe d'un
  //    import précédent mal aiguillé, ou du même fichier rechargé au mauvais
  //    endroit.
  const ailleurs = new Set();
  for (const e of existantes) {
    if (!e.compteId || e.compteId === compte.id) continue;
    ailleurs.add(`${e.dateBanque || e.date}|${e.sens === 'revenu' ? 'R' : 'D'}|${cents(e.montant)}`);
  }
  const collisions = operations.filter(o =>
    ailleurs.has(`${o.date}|${(o.montant || 0) >= 0 ? 'R' : 'D'}|${cents(o.montant)}`)).length;
  if (collisions > 0) {
    monter('attention');
    messages.push({ niveau: 'attention', cle: 'finances.vDejaAilleurs', params: { n: collisions } });
  }

  return { niveau, messages, resume: resumeReleve(operations) };
}
